import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { getLandlordDeals } from './deals-dashboard.api';
import type { GetLandlordDealsHTTPHandlerResponseData } from './application.types';
import { dealsTableKeys, type DealsListQuery } from './deals-table.query-keys';

/**
 * The deals list as a chain of pages. The API only returns the cursor for the next page, so each
 * page is requested with the cursor of the one before it; going back just reads a loaded page.
 * A new query (filters, sort, page size) starts a new chain, so no cursor outlives its filters.
 */
export const useFetchDeals = (query: DealsListQuery) =>
  useInfiniteQuery({
    queryKey: dealsTableKeys.list(query),
    queryFn: ({ pageParam }): Promise<GetLandlordDealsHTTPHandlerResponseData> =>
      getLandlordDeals(
        query.tab,
        query.pageLimit,
        pageParam,
        query.search,
        query.selectedStatus,
        query.selectedBuildings,
        undefined,
        query.leaseFilters,
        query.sortField,
        query.sortDirection,
        query.renewalOpportunityStatus,
        query.includeExpiredDeals,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.pagination?.page_cursor ?? undefined,
    staleTime: Infinity,
    // Keep the rows on screen while a filter, sort or page size changes, but never show one tab's
    // rows under another: the table stays mounted across tab switches.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2].tab === query.tab ? keepPreviousData(previous) : undefined,
    refetchOnWindowFocus: false,
  });
